<script setup>
/**
 * 旧版游戏页的界面还原外壳，供 liuhe / klsf / kuaisan 三个「仅界面、未接后端」的预览页共用。
 *
 * 这三个玩法在旧 Angular 发布包里存在独立页面模块（chunk 28 / 26 / 27），
 * 但后端 games/ 目录下没有对应游戏实现，因此这里只还原界面结构，
 * 所有数值均为占位数据，不发起任何下注或资金请求。
 */
import { computed, onMounted, onUnmounted, ref } from 'vue'

const props = defineProps({
  title: { type: String, required: true },
  period: { type: String, default: '' },
  // 倒计时起始秒数，仅用于让界面像旧版一样走秒，不代表真实盘态。
  countdown: { type: Number, default: 96 },
  totalBet: { type: [Number, String], default: 0 },
  balance: { type: [Number, String], default: 0 },
  unsettled: { type: [Number, String], default: 0 },
  profit: { type: [Number, String], default: 0 },
  selectedCount: { type: Number, default: 0 },
})
defineEmits(['reset'])

const tab = ref('play')
const showHistory = ref(false)
const seconds = ref(props.countdown)
let timer

const countdownText = computed(() => {
  const value = Math.max(0, seconds.value)
  return `${String(Math.floor(value / 60)).padStart(2, '0')}:${String(value % 60).padStart(2, '0')}`
})
function money(value) { return Number(value || 0).toFixed(2) }

onMounted(() => {
  timer = window.setInterval(() => {
    seconds.value = seconds.value > 0 ? seconds.value - 1 : props.countdown
  }, 1000)
})
onUnmounted(() => window.clearInterval(timer))
</script>

<template>
  <main class="preview-page">
    <header class="preview-toolbar">
      <RouterLink to="/home" class="preview-back" aria-label="返回">
        <svg viewBox="0 0 512 512" aria-hidden="true"><path d="M244 400 100 256l144-144M120 256h292" /></svg>
      </RouterLink>
      <strong>{{ title }}</strong>
      <span class="preview-badge">预览</span>
    </header>

    <p class="preview-hint" role="status">此页面按旧版界面还原，尚未接入后端，展示的是占位数据。</p>

    <nav class="preview-tabs">
      <button type="button" :class="{ active: tab === 'play' }" @click="tab = 'play'">下注</button>
      <button type="button" :class="{ active: tab === 'chat' }" @click="tab = 'chat'">聊天室</button>
    </nav>

    <section class="account-strip">
      <span>可用金额：<b>{{ money(balance) }}</b></span>
      <span>未结金额：<em>{{ money(unsettled) }}</em></span>
      <span>今日输赢：<em>{{ money(profit) }}</em></span>
    </section>

    <div v-if="tab === 'play'" class="preview-scroll">
      <section class="last-area">
        <div class="last-title">
          <span>{{ period || '—' }} 期</span>
          <slot name="lottery-summary" />
          <button type="button" @click="showHistory = !showHistory">{{ showHistory ? '收起' : '历史' }}</button>
        </div>
        <div class="last-numbers"><slot name="lottery" /></div>
      </section>

      <section v-if="showHistory" class="history-panel"><slot name="history" /></section>

      <section class="period-bar">
        <span>{{ period || '—' }} 期</span>
        <b>总投注：<em>{{ money(totalBet) }}</em></b>
        <span>封盘：{{ countdownText }}</span>
      </section>

      <nav class="bet-type-lists"><slot name="types" /></nav>
      <section class="bet-box"><slot name="board" /></section>
    </div>

    <section v-else class="preview-chat">
      <p>聊天室为旧版 WebSocket 功能，本预览页未接入。</p>
    </section>

    <footer v-if="selectedCount && tab === 'play'" class="bet-action">
      <button type="button" disabled title="本页未接入后端，无法下注">立即下注（已选 {{ selectedCount }} 注）</button>
      <button type="button" class="reset" @click="$emit('reset')">重 置</button>
    </footer>
  </main>
</template>

<style scoped>
.preview-page{display:flex;height:100vh;flex-direction:column;overflow:hidden;background:#f4f4f4;color:#111;font-family:Arial,"Microsoft YaHei",sans-serif;font-size:12px}
.preview-toolbar{display:grid;grid-template-columns:48px 1fr 48px;height:56px;align-items:center;background:#f04141;color:#fff}
.preview-back{display:grid;place-items:center;color:#fff;text-decoration:none}
.preview-back svg{width:22px;height:22px;fill:none;stroke:currentColor;stroke-width:32;stroke-linecap:round;stroke-linejoin:round}
.preview-toolbar strong{text-align:center;font-size:18px;font-weight:500}
.preview-badge{justify-self:end;margin-right:10px;border:1px solid rgb(255 255 255 / 60%);border-radius:2px;padding:1px 5px;font-size:11px}
.preview-hint{margin:0;padding:7px 12px;background:#fff8e1;color:#8a6d1f;font-size:11px;line-height:1.6}
.preview-tabs{display:grid;grid-template-columns:1fr 1fr;height:44px;border-bottom:1px solid #ddd;background:#fff}
.preview-tabs button{position:relative;border:0;background:#fff;color:#222;font-size:14px}
.preview-tabs .active{color:#ff545b}
.preview-tabs .active::after{position:absolute;right:35%;bottom:-1px;left:35%;height:2px;background:#ff545b;content:''}
.account-strip{display:flex;justify-content:space-between;padding:5px 12px;background:#d9f8fc;color:#333}
.account-strip b{color:#f04141;font-weight:400}
.account-strip em{color:#777;font-style:normal}
.preview-scroll{min-height:0;flex:1;overflow-x:hidden;overflow-y:scroll;scrollbar-gutter:stable;padding-bottom:60px}
.last-area{background:#fff;border-bottom:5px solid #f1f1f1}
.last-title{display:flex;height:34px;align-items:center;justify-content:space-between;padding:0 12px;border-bottom:1px solid #e5e5e5;color:#e8353d;font-size:13px}
.last-title button{border:0;background:transparent;color:#888;font-size:12px}
.last-numbers{display:flex;align-items:center;justify-content:center;gap:7px;padding:11px 12px}
.history-panel{background:#fff;border-bottom:5px solid #f1f1f1}
.period-bar{display:grid;grid-template-columns:1fr 1fr 1fr;height:37px;align-items:center;padding:0 12px;background:#fff;color:#444}
.period-bar b{text-align:center;font-weight:400}
.period-bar em{color:#e6373d;font-size:15px;font-style:normal}
.period-bar>span:last-child{text-align:right}
.bet-type-lists{display:flex;overflow-x:auto;border-top:1px solid #e5e5e5;border-bottom:1px solid #e5e5e5;background:#fff}
.bet-box{padding:12px;background:#389b61}
.preview-chat{display:grid;flex:1;place-items:center;color:#888}
.bet-action{position:fixed;right:0;bottom:0;left:0;z-index:20;display:flex;max-width:640px;margin:auto;gap:10px;padding:8px 15px 16px;background:#fff}
.bet-action button{height:39px;flex:1;border:0;border-radius:2px;background:#f04141;color:#fff}
.bet-action button:disabled{background:#ccc}
.bet-action .reset{flex:0 0 80px;background:#51cdcc}
@media (min-width:641px){.preview-page{max-width:640px;margin:auto;box-shadow:0 0 20px rgb(0 0 0 / 13%)}}
</style>
